import { Content, Header, Page } from '@backstage/core-components';
import React from 'react';
import AccountTreeIcon from '@material-ui/icons/AccountTree';
import BallotIcon from '@material-ui/icons/Ballot';
import DashboardIcon from '@material-ui/icons/Dashboard';
import DomainIcon from '@material-ui/icons/Domain';
import { Box, Card, CardActionArea, CardContent, CardHeader, Divider, Typography } from '@mui/material';
import { REQUEST_TYPE_LABEL_MAP } from './mappings';
import { useNavigate } from 'react-router';
import { RequestType } from '@internal/backstage-plugin-request-manager-common';

type RequestItem = {
    name: RequestType;
    icon: React.ReactNode;
    description: string;
    whenToUse: string;
    priority?: boolean;
}

const requestCards: RequestItem[] = [
    {
        name: 'component',
        icon: <BallotIcon />,
        description: 'Technical component (service, website or library) linked to a product. Includes repository, basic structure and pipelines.',
        whenToUse: 'Whenever you start developing a new service or module.',
        priority: true
    },
    {
        name: 'resource',
        icon: <DashboardIcon />,
        description: 'Infrastructure needed for your product (e.g. database, bucket, cluster).',
        whenToUse: 'After the product has been created, to ensure the team can start development with the right infrastructure.',
        priority: true
    },
    {
        name: 'product',
        icon: <DomainIcon />,
        description: 'A new product (System) in the catalog, with technical data, responsible parties, and integrations already configured.',
        whenToUse: 'After the structure (group/domain) has been created and approved.'
    },
    {
        name: 'domain-structure',
        icon: <AccountTreeIcon />,
        description: 'Your product\'s organizational structure, including group (team) and domain (product area).',
        whenToUse: 'Before you create the system (product) itself, ensuring it\'s connected to the correct structure in the catalog.'
    },
];

const RequestCard = ({ requestItem }: { requestItem: RequestItem }) => {
    const navigate = useNavigate();
    const onSelectRequest = () => navigate(requestItem.name);

    return (
        <Box
            sx={{
                width: requestItem.priority
                    ? { sm: '100%', md: '48%', lg: '30%' }
                    : '100%',
                maxWidth: requestItem.priority ? 600 : '100%',
                minWidth: 350,
            }}
        >
            <Card
                variant="outlined"
                sx={{
                    minHeight: requestItem.priority ? 200 : 'auto',
                    height: '100%',
                    boxShadow: 3,
                }}
            >
                <CardActionArea onClick={onSelectRequest} sx={{ height: '100%', width: '100%' }}>
                    <CardContent>
                        <Box display="flex" flexDirection="column" gap={1}>
                            <Box display="flex" alignItems="center" mb={1}>
                                <Box mr={2}>{requestItem.icon}</Box>
                                <Typography variant='h6' component="div">
                                    <strong>{REQUEST_TYPE_LABEL_MAP[requestItem.name]}</strong>
                                </Typography>
                            </Box>
                            <Box ml={5}>
                                <Typography variant="body1" component="div">
                                    <strong>What will be created:</strong> {requestItem.description}
                                </Typography>
                                <Typography variant="body1" component="div">
                                    <strong>When to use:</strong> {requestItem.whenToUse}
                                </Typography>
                            </Box>
                        </Box>
                    </CardContent>
                </CardActionArea>
            </Card>
        </Box>
    );
}

export const NewRequestPage = () => {
    return (
        <Page themeId="home">
            <Header title="New Request" type="Requests" typeLink='/requests' />
            <Content>
                <Card>
                    <CardHeader title="Create a new request" subheader="Select the type of request you want to create. Each type has a different purpose and will generate different items." />
                    <CardContent>
                        <Divider />
                        <Box display="flex" justifyContent="center" flexWrap="wrap" gap={3} mt={4}>
                            {requestCards
                                .filter(requestCard => requestCard.priority)
                                .map((requestCard) => (
                                    <RequestCard key={requestCard.name} requestItem={requestCard} />
                                ))
                            }
                        </Box>

                        <Box display="flex" justifyContent="center" flexWrap="wrap" gap={3} mt={4}>
                            {requestCards
                                .filter(requestCard => !requestCard.priority)
                                .map((requestCard) => (
                                    <RequestCard key={requestCard.name} requestItem={requestCard} />
                                ))
                            }
                        </Box>
                    </CardContent>
                </Card>
            </Content>
        </Page>
    );
};
